import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import { UserModel } from '../../../user.model';
import {
    DataFieldEditRolesComponent_Settings,
    TDataFieldEditRolesComponent_Settings,
} from '@fi-app/data-field/data-field-edit/roles/roles.component';
import { AccessRightFilterCollectionModel, ILightWeightBusinessUnit, ILightWeightObject } from '@fi-shared/models';
import {
    DataFieldEditUnitsComponent_Settings,
    TDataFieldEditUnitsComponent_Settings,
} from '@fi-app/data-field/data-field-edit/units/units.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    DataFieldEditBusinessAreasComponent_Settings,
    TDataFieldEditBusinessAreasComponent_Settings,
} from '@fi-app/data-field/data-field-edit/business-areas/business-areas.component';
import {
    DataFieldEditTagsComponent_Settings,
    TDataFieldEditTagsComponent_Settings,
} from '@fi-app/data-field/data-field-edit/tags/tags.component';
import { UserService } from '@fi-app/user/user.service';
import { takeUntil } from 'rxjs/operators';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiUserHelperService } from '@fi-shared/fi-user-shared/fi-user-helper.service';

@Component({
    selector: 'fi-user-edit-work-profile-tab',
    templateUrl: './fi-user-edit-work-profile-tab.component.html',
    styleUrls: ['./fi-user-edit-work-profile-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-edit-work-profile-tab' },
    providers: [
        {
            provide: DataFieldEditRolesComponent_Settings,
            deps: [FiUserEditWorkProfileTabComponent],
            useFactory: (
                userEditComponent: FiUserEditWorkProfileTabComponent,
            ): TDataFieldEditRolesComponent_Settings => ({
                lightWeightRoles: userEditComponent.lightWeightRoles,
                AccessRightFilter: userEditComponent.isADLogin
                    ? [-1]
                    : userEditComponent.accessRightFilterCollection.AccessRightCoreFilter.RoleIds,
                dataFieldItemProperty: 'UserToRoleIds',
                isDisabledFunc: () => userEditComponent.isRolesDisabled,
                isChipsSearchLayout: true,
            }),
        },
        {
            provide: DataFieldEditUnitsComponent_Settings,
            deps: [FiUserEditWorkProfileTabComponent, FiCustomerDataService],
            useFactory: (
                userEditComponent: FiUserEditWorkProfileTabComponent,
            ): TDataFieldEditUnitsComponent_Settings => ({
                lightWeightBusinessUnits: userEditComponent.lightWeightBusinessUnits,
                AccessRightFilter: userEditComponent.isADLogin
                    ? [-1]
                    : userEditComponent.availableBusinessUnits.map((item) => item.Id),
                dataFieldItemProperty: 'UserToBusinessUnitIds',
                dataFieldItemPropertyPreferred: 'PreferredBusinessUnitId',
                showSelectAll: !userEditComponent.isADLogin,
                onBusinessUnitsChange: () => userEditComponent.onUserToBusinessUnitIdsChange(),
                isDisabledFunc: (businessUnit: ILightWeightBusinessUnit) => {
                    if (userEditComponent.isBusinessUnitsDisabled) return true;
                    // https://chainformation.atlassian.net/browse/FI-5482
                    // disable units which are PK ones
                    return userEditComponent.isBusinessUnitsDisabledByPKUnits(businessUnit);
                },
                isChipsSearchLayout: true,
                isDeactivatedLabelShown: true,
            }),
        },
        {
            provide: DataFieldEditBusinessAreasComponent_Settings,
            deps: [FiUserEditWorkProfileTabComponent],
            useFactory: (
                userEditComponent: FiUserEditWorkProfileTabComponent,
            ): TDataFieldEditBusinessAreasComponent_Settings => ({
                accessRightFilterCollection: userEditComponent.accessRightFilterCollection,
                dataFieldItemProperty: 'UserToBusinessAreaIds',
                lightWeightBusinessAreas$: userEditComponent.lightWeightBusinessAreas$,
                isDisabledFunc: () => true,
                isNewSelectIcon: true,
            }),
        },
        {
            provide: DataFieldEditTagsComponent_Settings,
            deps: [FiUserEditWorkProfileTabComponent],
            useFactory: (
                userEditComponent: FiUserEditWorkProfileTabComponent,
            ): TDataFieldEditTagsComponent_Settings => ({
                tagsDataSource: () => userEditComponent.userService.getUserTagsByUserAccessRights(),
                isDisabledFunc: () => userEditComponent.isTagsDisabled,
            }),
        },
        { provide: FiComponentsContainer, useExisting: FiUserEditWorkProfileTabComponent },
    ],
    standalone: false,
})
export class FiUserEditWorkProfileTabComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input() dataFieldsRoles: DataFieldModel[] = [];
    @Input() dataFieldsUnits: DataFieldModel[] = [];
    @Input() dataFieldsBusinessAreas: DataFieldModel[] = [];
    @Input() dataFieldsTags: DataFieldModel[] = [];
    @Input() user: UserModel;
    @Input() accessRightFilterCollection: AccessRightFilterCollectionModel;
    @Input() isADLogin: boolean;
    @Input() hasEditAccess: boolean;
    @Input() lightWeightBusinessUnits: ILightWeightObject[];
    @Input() availableBusinessUnits: ILightWeightObject[];
    @Input() lightWeightBusinessAreas: ILightWeightBusinessAreaWithUnitId[];
    @Input() updateBusinessAreas$ = new Observable<void>();
    @Input() initLightWeightRoles$ = new Observable<void>();

    lightWeightBusinessAreas$ = new BehaviorSubject<ILightWeightObject[]>([]);
    lightWeightRoles: ILightWeightObject[] = [];

    isBusinessUnitsDisabled: boolean;
    isRolesDisabled: boolean;
    isBusinessAreasDisabled: boolean;
    isTagsDisabled: boolean;

    constructor(
        private userService: UserService,
        private userHelperService: FiUserHelperService,
        private customerDataService: FiCustomerDataService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.setSubscriptionsOnDataUpdate();
        this._initLightWeightRoles();

        this.isBusinessUnitsDisabled = this.isBusinessAreasDisabled = this.isPKUser() || !this.hasEditAccess;
        this.isTagsDisabled = this.isRolesDisabled = !this.hasEditAccess;
        this.lightWeightBusinessAreas$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((businessAreas) => {
            this.user.UserToBusinessAreaIds = businessAreas.map((businessArea) => businessArea.Id);
        });
    }

    public isBusinessUnitsDisabledByPKUnits(businessUnit: ILightWeightBusinessUnit) {
        return this.userHelperService.isBusinessUnitsDisabledByPKUnits(businessUnit);
    }

    private isPKUser() {
        return this.userHelperService.isPKUser(this.user);
    }

    private setSubscriptionsOnDataUpdate() {
        this.updateBusinessAreas$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.updateBusinessAreas());
        this.initLightWeightRoles$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this._initLightWeightRoles());
    }

    private _initLightWeightRoles() {
        this.lightWeightRoles = this.accessRightFilterCollection.AccessRightCoreFilter.Roles.slice();
        this.user.Roles.forEach((role) => {
            // if user role is not already in the list then add it
            if (this.accessRightFilterCollection.AccessRightCoreFilter.RoleIds.indexOf(role.Id) === -1)
                this.lightWeightRoles.push(role);
        });
    }

    onUserToBusinessUnitIdsChange() {
        this.updateBusinessAreas();
    }

    protected updateBusinessAreas() {
        let businessAreas: ILightWeightObject[] = [];
        if (this.user.UserToBusinessUnitIds.length > 0) {
            businessAreas = this.getBusinessAreasForUserBusinessUnits();

            const loadedAreasIds = businessAreas.map((ba) => ba.Id);
            const newUserAreasIds = [];
            this.user.UserToBusinessAreaIds.forEach((id) => {
                if (loadedAreasIds.indexOf(id) > -1) newUserAreasIds.push(id);
            });

            this.user.UserToBusinessAreaIds.length = 0;
            newUserAreasIds.forEach((id) => this.user.UserToBusinessAreaIds.push(id));
        }

        if (!this.user.UserToBusinessUnitIds.length) {
            this.user.PreferredBusinessUnitId = undefined;
        } else if (
            this.user.UserToBusinessUnitIds.indexOf(this.user.PreferredBusinessUnitId) === -1 ||
            (!this.user.PreferredBusinessUnitId && !!this.user.UserToBusinessUnitIds.length)
        ) {
            this.user.PreferredBusinessUnitId = this.user.UserToBusinessUnitIds[0];
        }

        this.lightWeightBusinessAreas$.next(businessAreas);
    }

    private getBusinessAreasForUserBusinessUnits() {
        const businessAreas = [];
        this.lightWeightBusinessAreas.forEach((businessArea) => {
            if (
                this.user.UserToBusinessUnitIds.indexOf(businessArea.BusinessUnitId) > -1 &&
                !businessAreas.filter((item) => item.Id == businessArea.Id).length
            )
                businessAreas.push(businessArea);
        });
        return businessAreas;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
